import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from "../../hooks/useAuthPatched";
import axios from 'axios';
import { getApiUrl } from '../../config';
import { Box, CircularProgress } from '@mui/material';
import ProfileCompletionPage from './ProfileCompletionPage';

const ProfileRequiredRoute = ({ children, skipProfileCheck = false }) => {
    const { user, getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
    const location = useLocation();
    const [profileStatus, setProfileStatus] = useState({
        loading: true,
        hasCompleteProfile: false,
        missingFields: [],
        profileData: {}
    });

    const handleComplete = (profile) => {
        setProfileStatus((prev) => ({
            loading: false,
            hasCompleteProfile: true,
            missingFields: [],
            profileData: profile || prev.profileData
        }));
    };

    useEffect(() => {
        const checkProfileCompletion = async () => {
            if (!isAuthenticated || !user || skipProfileCheck) {
                setProfileStatus({ loading: false, hasCompleteProfile: true, missingFields: [] });
                return;
            }

            try {
                const token = await getAccessTokenSilently();
                const response = await axios.get(`${getApiUrl('javaBackend')}/api/user/profile`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const profile = response.data;
                const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'zip'];
                const missingFields = [];

                // Check which required fields are missing
                requiredFields.forEach(field => {
                    if (!profile[field] || profile[field].trim() === '') {
                        missingFields.push(field);
                    }
                });

                setProfileStatus({
                    loading: false,
                    hasCompleteProfile: missingFields.length === 0,
                    missingFields,
                    profileData: profile
                });
            } catch (error) {
                console.error('Error checking profile:', error);
                
                // If profile doesn't exist at all, all fields are missing
                if (error.response?.status === 404) {
                    setProfileStatus({
                        loading: false,
                        hasCompleteProfile: false,
                        missingFields: ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'zip'],
                        profileData: {}
                    });
                } else {
                    // On error, allow access but log the issue
                    console.error('Profile check failed, allowing access:', error);
                    setProfileStatus({ 
                        loading: false, 
                        hasCompleteProfile: true, 
                        missingFields: [],
                        profileData: {}
                    });
                }
            }
        };

        checkProfileCompletion();
    }, [isAuthenticated, user, getAccessTokenSilently, skipProfileCheck]);

    // Show loading state
    if (isLoading || profileStatus.loading) {
        return (
            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="100vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    // If not authenticated, let the parent ProtectedRoute handle it
    if (!isAuthenticated) {
        return children;
    }

    // Check if trying to access settings page (allowed for profile completion)
    const isSettingsPage = location.pathname.includes('/settings');

    // If profile is incomplete and not on settings page, show modern completion page
    if (!profileStatus.hasCompleteProfile && !isSettingsPage) {
        return (
            <ProfileCompletionPage
                missingFields={profileStatus.missingFields}
                initialData={profileStatus.profileData}
                onComplete={handleComplete}
            />
        );
    }

    // Profile is complete or on settings page, allow access
    return children;
};

export default ProfileRequiredRoute;
